import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import InfiniteSearchSelect from '@/components/shared/InfiniteSearchSelect';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import { Checkbox, SearchSelect, Textarea } from '@/components/ui/daisy';
import { studentsQuerykeys, usersQueryKeys } from '@/data/services/querykeys';
import { OccurrenceRecord } from '@/utils/assessments/klassPedagogicalSheetRecordTypes';
import User, { UserStatusEnum } from '@/models/User';
import { occurrenceToFormFields } from '@/utils/assessments/occurrenceToFormFields';
import { RecordFormShell } from '@/components/shared/RecordFormShell';

export type variant = 'default' | 'compact';

export type OccurrenceFormFields = {
  studentId: number | null;
  description: string;
  notificable: boolean;
};

export type OccurrenceFormContext = {
  klassId?: number;
  studentId?: number;
};

export type OccurrenceFormProps = {
  record?: OccurrenceRecord;
  isLoading?: boolean;
  onSubmit: (data: OccurrenceFormFields) => void;
  onCancel: () => void;
  variant?: variant;
} & OccurrenceFormContext;

export default function OccurrenceForm({
  klassId,
  studentId,
  record,
  isLoading,
  onSubmit,
  onCancel,
  variant = 'default',
}: OccurrenceFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'klassDetail.pedagogicalSheet.forms',
  });
  const { t: tOccurrence } = useTranslation('translation', {
    keyPrefix: 'occurrenceAssessmentForm',
  });

  const isEditing = !!record;
  const assessment = record?.assessment;
  const isCompact = variant === 'compact';

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<OccurrenceFormFields>({
    defaultValues: {
      ...occurrenceToFormFields(assessment),
      ...(studentId ? { studentId } : {}),
    },
  });

  return (
    <RecordFormShell
      hasChanges={isDirty}
      isSaving={isLoading}
      onSubmit={handleSubmit(onSubmit)}
      onCancel={onCancel}
    >
      <RecordFormShell.Title className={isCompact ? 'text-sm' : undefined}>
        {isEditing ? t('editOccurrence') : t('newOccurrence')}
      </RecordFormShell.Title>

      <ConditionalRenderer condition={!studentId}>
        <Controller
          name="studentId"
          control={control}
          rules={{
            required: t('studentRequired'),
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <SearchSelect.Fieldset
              label={t('student')}
              labelAlt={error?.message}
              className="max-w-sm"
            >
              <InfiniteSearchSelect
                listService={studentsQuerykeys.list}
                getService={id => usersQueryKeys.get(Number(id))}
                filters={{
                  status: [UserStatusEnum.ACTIVE_ENROLLMENT],
                  klassId,
                }}
                getOptionLabel={(student: User) =>
                  `${student.firstName} ${student.lastName}`
                }
                value={value}
                onChange={student => onChange(student?.id ?? null)}
                placeholder={t('studentPlaceholder')}
                error={!!error}
                disabled={isEditing}
              />
            </SearchSelect.Fieldset>
          )}
        />
      </ConditionalRenderer>

      <Controller
        name="description"
        control={control}
        rules={{
          required: t('descriptionRequired'),
        }}
        render={({ field, fieldState: { error } }) => (
          <Textarea.Fieldset labelAlt={error?.message}>
            <Textarea
              {...field}
              className="min-h-24 w-full"
              placeholder={tOccurrence(
                'descriptionTextArea.placeholder.student',
              )}
              error={!!error}
            />
          </Textarea.Fieldset>
        )}
      />

      <div className="flex items-center gap-1 self-end">
        <Controller
          name="notificable"
          control={control}
          render={({ field: { value, onChange } }) => (
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                color="primary"
                checked={value}
                disabled={isEditing}
                onChange={event => onChange(event.target.checked)}
              />
              <span
                className={twMerge(
                  isCompact
                    ? 'text-base-content/60 text-xs'
                    : 'text-base-content text-sm',
                )}
              >
                {tOccurrence('notificableCheckbox.label.student')}
              </span>
            </label>
          )}
        />
        <ConditionalRenderer condition={!isCompact}>
          <Tooltip
            text={tOccurrence('notificableCheckbox.info.student')}
            className="max-w-96"
          >
            <InformationCircleIcon className="size-4 text-primary" />
          </Tooltip>
        </ConditionalRenderer>
      </div>

      <ConditionalRenderer condition={!isCompact}>
        <RecordFormShell.Datetime value={assessment?.createdAt} />
      </ConditionalRenderer>

      <RecordFormShell.Actions
        size={isCompact ? 'xs' : 'md'}
        className={isCompact ? 'justify-end' : undefined}
      />
    </RecordFormShell>
  );
}
